// A note for customers browsing from outside the UK.
//
// Every brand in the catalogue is UK based, so ordering from elsewhere can be
// awkward. Rather than let someone find that out at a retailer's checkout, we
// say it once, warmly, and point at what is still useful to them.
//
// THE COPY IS SIGNED OFF AND VERBATIM. Adrian approved these exact words. Do
// not reword, shorten or restructure them without asking, and note the third
// paragraph is deliberately bold: it is the part he wants read.
//
// NO DASHES. This repo fails its build on an em or en dash in customer copy,
// and a release note is not the only string that reaches customers. If a
// sentence here needs restructuring, restructure it.
//
// FREQUENCY lives in lib/outside-uk-notice.ts, shared with the mobile screen so
// the two cannot drift on the rule. Read the note at the top of that file
// before changing anything here: the detection fails CLOSED and the storage
// fails OPEN, deliberately and in opposite directions.

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  recordShown,
  shouldShowNotice,
  suppressForever,
} from "@/lib/outside-uk-notice";
import { cn } from "@/lib/utils";

const SERVICES_URL =
  "https://www.mariapersonalshopper.com/personal-shopper-london";

export function OutsideUKNotice() {
  const [open, setOpen] = useState(false);
  const [suppress, setSuppress] = useState(false);

  // Decided after mount, never during render: the prerendered HTML that
  // scripts/prerender.js writes must not contain this, and `Intl` is a browser
  // answer. The same reason AppDownloadBanner renders client-only.
  useEffect(() => {
    if (!shouldShowNotice()) return;
    // Counted on SHOW, not on dismiss. A force quit skips the dismissal, and
    // counting there would hand out an extra view every time.
    recordShown();
    setOpen(true);
  }, []);

  if (!open) return null;

  const close = () => {
    if (suppress) suppressForever();
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-charcoal/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="outside-uk-notice-title"
      data-testid="outside-uk-notice"
    >
      <div
        className={cn(
          "w-full max-w-lg rounded-2xl bg-warm-white p-6 shadow-xl",
          "dark:bg-gray-900 dark:text-gray-100",
        )}
      >
        <h2
          id="outside-uk-notice-title"
          className="font-playfair text-2xl font-semibold text-charcoal dark:text-gray-100"
        >
          A note on where I shop
        </h2>

        <p className="mt-4 text-charcoal/80 dark:text-gray-300">
          Right now every brand I stock is based in the UK, so ordering from
          elsewhere can be tricky or expensive.
        </p>

        <p className="mt-3 text-charcoal/80 dark:text-gray-300">
          Have a look around for the ideas: which colours suit you, which shapes
          work for your body, how I put a whole look together. Save anything you
          love, and you may well find something close to it where you are.
        </p>

        {/* Bold on purpose. This is the paragraph Adrian wants seen. */}
        <p className="mt-3 font-semibold text-charcoal dark:text-gray-100">
          I am working hard on making this better for the rest of the world, so
          do keep an eye out. And if you are ever coming to London, I would love
          to take you shopping in person.{" "}
          <a
            href={SERVICES_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-dusty-rose-ink"
            data-testid="outside-uk-notice-cta"
          >
            Book a personal shopping experience
          </a>
        </p>

        <label className="mt-6 flex items-center gap-2 text-sm text-charcoal/70 dark:text-gray-400">
          <Checkbox
            checked={suppress}
            onCheckedChange={(value) => setSuppress(value === true)}
            data-testid="outside-uk-notice-suppress"
          />
          Do not show this again
        </label>

        <div className="mt-4 flex justify-end">
          <Button onClick={close} data-testid="outside-uk-notice-dismiss">
            Keep looking
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OutsideUKNotice;
